import '../work-order.scss';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { useWorkOrderAPI } from '../../../clients/quipli-service/work-order/work-order-api';
import {
  UseabilityStatus,
  WorkOrder,
  WorkOrderStatus,
  WorkOrderType,
  WorkOrderNoteFile,
  WorkOrderProductInput,
  WorkOrderProduct,
} from '../../../clients/quipli-service/work-order/work-order';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { ProductImage } from '../../../components/image/product/product-image';
import { Spinner } from '../../../components/spinner/spinner';
import { displayMoneyFormat, formatPhoneNumber } from '../../../util/util';
import { Modal } from '../../../components/modal/modal';
import { TextArea } from '../../../components/textarea/textarea';
import { SelectForm } from '../../../components/select-form/select-form';
import { Tooltip } from '../../../components/tooltip/tooltip';
import { useParams } from 'react-router-dom';
import { OrderScheduleEntry } from '../../../clients/quipli-service/availability/availability';
import { OrderReturnStatus } from '../../../clients/quipli-service/order/order';
import { Button } from '../../../components/form/button';
import { QuipliLink } from '../../../components/link/quipli-link';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { useAdmin } from '../../../hooks/use-admin-context';
import { ConfirmModal } from '../../../components/modal/confirm-modal';
import { Badge } from '../../../components/badge/badge';
import { Timeline } from '../../../components/timeline/timeline';
import { Field } from '../../../components/field/field';
import { ProductImageV2 } from '../../../components/image/product/product-image-v2';
import { OrderSection } from '../../order/order-section/order-section';
import { InvoiceList } from '../../collectibles/invoice-list';
import { Dictionary, keyBy } from 'lodash';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { WorkOrderPartsTable } from './work-order-parts-table';
import { Location } from '../../../clients/quipli-service/location/location';
import { toast } from '../../../util/toasts';
import { LocationSelect } from '../../location/select/location-select';
import { QboInvoice } from '../../qbo/invoice/qbo-invoice-v2';
import { EmailHistory } from '../../order/email-history/email-history';
import { ValidationError } from '../../../components/validation-error/validation-error';

type ActivityItemProps = {
  text: JSX.Element;
  object?: WorkOrderNoteFile;
  color?: 'success' | 'warning';
  date: DateTime;
};

export const WorkOrderView = () => {
  const { workOrderId } = useParams();
  return <WorkOrderPage workOrderId={workOrderId as string} />;
};

export const WorkOrderPage = ({ workOrderId }: { workOrderId: string }) => {
  // Api
  const api = useAPI();
  const adminAPI = useAdminApiV1();
  const workOrderAPI = useWorkOrderAPI();

  // States
  const [workOrder, setWorkOrder] = useState<WorkOrder>(),
    [workOrderProducts, setWorkOrderProducts] = useState<WorkOrderProduct[]>(),
    [location, setLocation] = useState<Location | null>(),
    [locations, setLocations] = useState<Location[] | undefined>(),
    [isRequestInProgress, setRequestInProgress] = useState<boolean>(false),
    [listTypes, setListTypes] = useState<WorkOrderType[]>([]),
    [isEditable, setIsEditable] = useState<boolean>(false),
    [from, setFrom] = useState<DateTime>(),
    [to, setTo] = useState<DateTime>(),
    [type, setType] = useState<WorkOrderType | null>(null),
    [equipmentDescription, setEquipmentDescription] = useState<string>(),
    [unitStatus, setUnitStatus] = useState<{ label: string; value: string }>(),
    [unitTrackingField, setUnitTrackingField] = useState<'unitNumber' | 'serialNumber'>('unitNumber'),
    [productImagesV2, setProductImagesV2] = useState<boolean>(true),
    [currency, setCurrency] = useState<string>(),
    [displayTaxByJurisdiction, setDisplayTaxByJurisdiction] = useState<boolean>(),
    [mechanic, setMechanic] = useState<Admin | null>(null),
    [mechanics, setMechanics] = useState<Dictionary<Admin>>(),
    [poNumber, setPoNumber] = useState<string>(),
    [customerNotes, setCustomerNotes] = useState<string>(),
    [enableParts, setEnableParts] = useState<boolean>(false);

  // Callbacks
  const fetchWorkOrder = async () => {
    const wrkOrder = await workOrderAPI.getWorkOrderById(workOrderId);
    const lcation = await api.getLocationById(wrkOrder.locationId!);
    const allLocations = await api.listLocations(true);
    const [types] = await Promise.all([api.listWorkOrderTypes()]);

    setLocation(lcation);
    setLocations(allLocations);
    setListTypes(types);

    setWorkOrderDetails(wrkOrder);
  };

  const setWorkOrderDetails = (workOrder: WorkOrder) => {
    setWorkOrder(workOrder);
    setWorkOrderProducts(workOrder.workOrderProducts);
    setFrom(workOrder.status === WorkOrderStatus.NotStarted ? workOrder.from : undefined);
    setTo(workOrder.to);
    setType(workOrder.type ? workOrder.type : null);
    setEquipmentDescription(workOrder.equipmentDescription);
    setUnitStatus(
      workOrder.unitStatus
        ? { label: workOrder.unitStatus, value: workOrder.unitStatus.replace(/\s/g, '') }
        : undefined,
    );
    setMechanic(workOrder.mechanic || null);
    setPoNumber(workOrder.poNumber);
    setCustomerNotes(workOrder.notes);
  };

  const handleUpdate = async (closeEditable?: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (isRequestInProgress || !workOrder) return;

    setRequestInProgress(true);

    const wrkOrder = await workOrderAPI.updateWorkOrder({
      id: workOrder.id,
      from: from,
      to: to,
      type: type?.id,
      unitStatus: unitStatus?.value,
      equipmentDescription: equipmentDescription,
      locationId: location?.id,
      mechanicId: mechanic?.adminId,
      poNumber: poNumber,
      notes: customerNotes,
    });

    setWorkOrderDetails(wrkOrder);
    closeEditable && closeEditable(false);
    setRequestInProgress(false);
  };

  const handleOnAddPart = async (input: WorkOrderProductInput) => {
    if (isRequestInProgress || !workOrder) return;

    let updatedWorkOrder: WorkOrder;
    try {
      updatedWorkOrder = await workOrderAPI.createWorkOrderProduct(workOrder.id, input);
    } catch (e: any) {
      if (e.status === 400) {
        toast(e.message, 'error');
      } else {
        toast('Error adding part', 'error');
      }
      return;
    }

    setWorkOrderDetails(updatedWorkOrder);
  };

  const handleOnDeletePart = async (workOrderPartId: string) => {
    if (isRequestInProgress || !workOrder) return;

    let updatedWorkOrder: WorkOrder;
    try {
      updatedWorkOrder = await workOrderAPI.deleteWorkOrderProduct(workOrder.id, workOrderPartId);
    } catch (e: any) {
      if (e.status === 400) {
        toast(e.message, 'error');
      } else {
        toast('Error removing part', 'error');
      }
      return;
    }

    setWorkOrderDetails(updatedWorkOrder);
  };

  const handleOnEditPart = async (input: WorkOrderProductInput) => {
    if (isRequestInProgress || !workOrder) return;

    let updatedWorkOrder: WorkOrder;
    try {
      updatedWorkOrder = await workOrderAPI.updateWorkOrderProduct(workOrder.id, input);
    } catch (e: any) {
      if (e.status === 400) {
        toast(e.message, 'error');
      } else {
        toast('Error updating part', 'error');
      }
      return;
    }

    setWorkOrderDetails(updatedWorkOrder);
  };

  // Hooks
  useEffect(() => {
    fetchWorkOrder();
  }, [workOrderId]);

  useEffect(() => {
    const getConfigurations = async () => {
      const [configs, admins] = await Promise.all([
        api.getMultipleConfigurations([
          'CONF_CURRENCY',
          'CONF_ENABLE_PARTS',
          'CONF_PRODUCT_IMAGES_V2',
          'CONF_UNIT_TRACKING_VIA',
          'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN',
        ]),
        adminAPI.getAdminsList(),
      ]);

      setEnableParts(configs.find((c) => c.variable === 'CONF_ENABLE_PARTS')?.value === '1');
      setUnitTrackingField(
        configs.find((c) => c.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
      );
      setProductImagesV2(configs.find((c) => c.variable === 'CONF_PRODUCT_IMAGES_V2')?.value === '1');
      setCurrency(configs.find((c) => c.variable === 'CONF_CURRENCY')?.value || 'USD');
      setDisplayTaxByJurisdiction(
        configs.find((c) => c.variable === 'CONF_DISPLAY_JURISDICTION_TAXES_BREAKDOWN')?.value === '1',
      );
      setMechanics(keyBy(admins, 'adminId'));
    };

    getConfigurations();
  }, []);

  return (
    <div className="work-order">
      {workOrder === undefined ? (
        <Spinner />
      ) : (
        <>
          <div className="work-order-details">
            <div className="grid grid-cols-1">
              {/* Details */}
              <div className="col-span-4 px-5 md:px-16 py-8">
                <div className="flex justify-between items-center">
                  <div className="flex items-left items-baseline">
                    <Typography type="display-xs" weight="medium" className="text-grey-800">
                      Work Order details
                    </Typography>
                    {workOrder.orderNumber && <span className="text-grey-500 ml-2">({workOrder.orderNumber})</span>}
                  </div>

                  {workOrder.status != WorkOrderStatus.Completed &&
                    workOrder.status != WorkOrderStatus.Cancelled &&
                    !isRequestInProgress && (
                      <Tooltip tooltip={'Edit details'} type="black" invisible={isEditable}>
                        <div
                          className={classNames('object-action', { 'close-action': isEditable })}
                          onClick={() => setIsEditable(!isEditable)}
                        >
                          <Icon icon={isEditable ? 'Close' : 'Edit'} size="medium" />
                        </div>
                      </Tooltip>
                    )}
                </div>

                <div className="flex items-center gap-x-4 py-7">
                  {workOrder.product && (
                    <>
                      <div className="image-container">
                        {productImagesV2 ? (
                          <ProductImageV2 productId={workOrder.product.id} profile="THUMB" />
                        ) : (
                          <ProductImage productId={workOrder.product.id} profile="THUMB" />
                        )}{' '}
                      </div>
                      <Typography
                        type="text-lg"
                        weight="medium"
                        className="flex flex-col md:flex-row gap-4 text-grey-800"
                      >
                        <span>{workOrder.product.name}</span>
                        {workOrder.unit && (
                          <>
                            <span className="hidden md:inline-block">-</span>
                            <span>
                              <b>#</b> {workOrder.unit ? workOrder.unit[unitTrackingField] : '-'}
                            </span>
                          </>
                        )}
                      </Typography>
                    </>
                  )}

                  {workOrder.userEquipment && (
                    <Typography
                      type="text-lg"
                      weight="medium"
                      className="truncate flex flex-col md:flex-row gap-4 text-grey-800"
                    >
                      <span>
                        {workOrder.userEquipment.make
                          ? `${workOrder.userEquipment.make} ${workOrder.userEquipment.model}`
                          : workOrder.userEquipment.description}
                        {workOrder.userEquipment.serialNumber && ` - ${workOrder.userEquipment.serialNumber}`}
                      </span>
                    </Typography>
                  )}
                </div>

                <div className="h-[1px] w-full bg-grey-300"></div>

                <div className="fields-section text-sm">
                  {workOrder.user && (
                    <div className="field" style={{ alignItems: 'start' }}>
                      <Typography type="text-sm" weight="medium" className="property text-grey-500">
                        Customer
                      </Typography>

                      <Typography type="text-sm" className="text-grey-800">
                        <QuipliLink
                          target="_blank"
                          to={`users/customer_form/${workOrder.user.id}`}
                          className="text-link-200 underline w-fit"
                        >
                          {workOrder.user.name}
                        </QuipliLink>
                        <div>{workOrder.user.email}</div>
                        <div>{formatPhoneNumber(workOrder.user.phone)}</div>
                      </Typography>
                    </div>
                  )}

                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-500">
                      Work Start
                    </Typography>

                    {isEditable && workOrder.actualStart === undefined ? (
                      <DateTimePicker value={from} timezone={location?.timezone} onChange={(v) => setFrom(v)} />
                    ) : (
                      <Typography type="text-sm" className="date-value text-grey-800">
                        {workOrder.status === WorkOrderStatus.Completed && workOrder.actualStart ? (
                          <>
                            <span>{`${workOrder.actualStart.setZone(location?.timezone).toFormat('DD')}`}</span>
                            <span>{`${workOrder.actualStart.setZone(location?.timezone).toFormat('t')}`}</span>
                          </>
                        ) : (
                          <>
                            <span>{`${workOrder.from.setZone(location?.timezone).toFormat('DD')}`}</span>
                            <span>{`${workOrder.from.setZone(location?.timezone).toFormat('t')}`}</span>
                          </>
                        )}
                      </Typography>
                    )}
                  </div>

                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-500">
                      Work End
                    </Typography>

                    {isEditable ? (
                      <DateTimePicker value={to} timezone={location?.timezone} onChange={(v) => setTo(v)} clearable />
                    ) : (
                      <Typography type="text-sm" className="date-value text-grey-800">
                        {workOrder.status === WorkOrderStatus.Completed && workOrder.actualEnd ? (
                          <>
                            <span>{`${workOrder.actualEnd.setZone(location?.timezone).toFormat('DD')}`}</span>
                            <span>{`${workOrder.actualEnd.setZone(location?.timezone).toFormat('t')}`}</span>
                          </>
                        ) : workOrder.to ? (
                          <>
                            <span>{`${workOrder.to.setZone(location?.timezone).toFormat('DD')}`}</span>
                            <span>{`${workOrder.to.setZone(location?.timezone).toFormat('t')}`}</span>
                          </>
                        ) : (
                          <div>TBD</div>
                        )}
                      </Typography>
                    )}
                  </div>

                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-500">
                      Type of Work
                    </Typography>
                    {isEditable ? (
                      <SelectForm
                        options={listTypes}
                        value={type}
                        onChange={(option) => setType(option)}
                        getOptionValue={(option) => option.id}
                        styles={{
                          control: () => ({
                            backgroundColor: '#F9FAFB',
                          }),
                        }}
                        className="min-w-[243px]"
                      />
                    ) : (
                      <Typography type="text-sm" className="text-grey-800">
                        {workOrder.type?.label ?? '-'}
                      </Typography>
                    )}
                  </div>

                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-500">
                      PO Number
                    </Typography>

                    {isEditable ? (
                      <Field type="text" value={poNumber} onChange={(v) => setPoNumber(v)} />
                    ) : (
                      <Typography type="text-sm" className="date-value text-grey-800">
                        {workOrder.poNumber}
                      </Typography>
                    )}
                  </div>

                  {workOrder.unitStatus &&
                    workOrder.status != WorkOrderStatus.Cancelled &&
                    workOrder.status != WorkOrderStatus.Completed && (
                      <div className="field">
                        <Typography type="text-sm" weight="medium" className="property text-grey-500">
                          Unit Availability
                        </Typography>
                        {isEditable ? (
                          <SelectForm
                            options={Object.keys(UseabilityStatus).map((key) => ({
                              label: UseabilityStatus[key as keyof typeof UseabilityStatus],
                              value: key,
                            }))}
                            value={unitStatus}
                            onChange={(option) => setUnitStatus(option!)}
                            styles={{
                              control: () => ({
                                backgroundColor: '#F9FAFB',
                              }),
                            }}
                            className="min-w-[243px]"
                          />
                        ) : (
                          <div className="grow">
                            {workOrder.unitStatus && (
                              <Badge
                                text={workOrder.unitStatus}
                                type={workOrder.unitStatus === UseabilityStatus.HardDown ? 'danger' : 'warning'}
                                showDot
                              />
                            )}
                          </div>
                        )}
                      </div>
                    )}

                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-500">
                      Status
                    </Typography>
                    <div className="flex gap-x-3 items-center">
                      <StatusIcon status={workOrder.status} />
                      <Typography type="text-sm" className="flex-shrink-0 text-grey-800">
                        {workOrder.status}
                      </Typography>
                    </div>
                  </div>

                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-500">
                      Location
                    </Typography>
                    <div className="flex gap-x-3 items-center">
                      {isEditable ? (
                        <LocationSelect
                          locations={locations}
                          setLocation={setLocation}
                          dataCy="location-select"
                          orderLocation={location ?? undefined}
                        />
                      ) : (
                        <Typography type="text-sm" className="flex-shrink-0 text-grey-800">
                          {location?.name || 'N/A'}
                        </Typography>
                      )}
                    </div>
                  </div>

                  <div className="field">
                    <Typography type="text-sm" weight="medium" className="property text-grey-500">
                      Mechanic
                    </Typography>
                    <div className="flex gap-x-3 items-center">
                      {isEditable ? (
                        <SelectForm
                          maxMenuHeight={200}
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          className="min-w-[250px] w-full"
                          isClearable={true}
                          isOptionSelected={(o) => workOrder.mechanic?.adminId === o.adminId}
                          options={Object.values(mechanics ?? []).map((d) => ({ ...d }))}
                          value={mechanic}
                          getOptionLabel={(o) => o.adminFullName}
                          onChange={(m) => setMechanic(m)}
                        />
                      ) : (
                        <Typography type="text-sm" className="flex-shrink-0 text-grey-800">
                          {workOrder.mechanic?.adminFullName || 'N/A'}
                        </Typography>
                      )}
                    </div>
                  </div>

                  {workOrder.user && (
                    <div className="field">
                      <Typography type="text-sm" weight="medium" className="property text-grey-500">
                        Customer Notes
                      </Typography>

                      {isEditable ? (
                        <TextArea
                          value={customerNotes}
                          onChange={({ target }) => setCustomerNotes(target.value)}
                          rows={3}
                          className="border-none w-full font-sans text-sm"
                        />
                      ) : (
                        <Typography type="text-sm" className="date-value text-grey-800">
                          {workOrder.notes}
                        </Typography>
                      )}
                    </div>
                  )}
                </div>

                {isRequestInProgress ? (
                  <div className={'pt-1'}>
                    <Spinner />
                  </div>
                ) : isEditable ? (
                  <div className="flex justify-end gap-[12px]">
                    <Button className="button-secondary" onClick={() => setIsEditable(false)}>
                      Cancel
                    </Button>
                    <Button className="button-primary" onClick={() => handleUpdate(setIsEditable)}>
                      Update
                    </Button>
                  </div>
                ) : (
                  <WorkFlow workOrder={workOrder} location={location!} refresh={fetchWorkOrder} />
                )}

                <div className="h-[1px] w-full bg-grey-300 mt-7"></div>

                {enableParts && location && (
                  <>
                    <div className="my-2">
                      <OrderSection className={classNames('flex flex-column gap-2')}>
                        <WorkOrderPartsTable
                          location={location}
                          workOrderProducts={workOrderProducts}
                          onAddPart={handleOnAddPart}
                          onDeletePart={handleOnDeletePart}
                          onEditPart={handleOnEditPart}
                          disabled={workOrder.status === WorkOrderStatus.Cancelled}
                        />
                      </OrderSection>
                    </div>

                    <div className="h-[1px] w-full bg-grey-300"></div>
                  </>
                )}

                {workOrder.user && currency && workOrder.invoices && displayTaxByJurisdiction !== undefined && (
                  <>
                    <div className="my-2">
                      <OrderSection className={classNames('order-invoices flex flex-column gap-2')} header="Invoices">
                        <InvoiceList
                          invoices={workOrder.invoices}
                          currency={currency}
                          refresh={() => fetchWorkOrder()}
                          enableInvoiceLink={false}
                          enabledColumns={[
                            'InvoiceNumber',
                            'DueDate',
                            'Date',
                            'Status',
                            'Total',
                            'Payments',
                            'Balance',
                            'Actions',
                          ]}
                          enablePaymentNotificationAction={false}
                          showCycleBillingActions={false}
                          displayTaxByJurisdiction={displayTaxByJurisdiction}
                        />
                      </OrderSection>

                      <div className="h-[1px] w-full bg-grey-300 my-2"></div>
                    </div>
                  </>
                )}

                {workOrder.invoices && workOrder.invoices.length > 0 && <QboInvoice workOrderId={workOrderId} />}

                {!workOrder.user && <LastAndUpcomingOrders workOrder={workOrder} />}
              </div>

              {/* Activity */}
              <div className="col-span-4 px-5 md:px-16">
                <Activity workOrder={workOrder} />
              </div>

              <div className="col-span-4 px-5 md:px-16">
                <div className="h-[1px] w-full bg-grey-300 mt-7"></div>
              </div>

              {/* Email History */}
              <div className="col-span-4 px-5 md:px-16 py-2">
                <EmailHistory orderId={workOrder.id} orderType="WorkOrder" orderDateAdded={workOrder.createdAt} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const WorkFlow = ({
  workOrder,
  location,
  refresh,
}: {
  workOrder: WorkOrder;
  location: Location;
  refresh: () => Promise<void>;
}) => {
  // Api
  const { updateWorkOrderStatus } = useAPI();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [workFrom, setWorkFrom] = useState<DateTime | undefined>();
  const [workTo, setWorkTo] = useState<DateTime | undefined>();
  const [actualCost, setActualCost] = useState<number>();
  const [hourReading, setHourReading] = useState<number>();
  const [action, setAction] = useState<'Start' | 'Complete' | 'Cancel' | 'Resume' | 'On Hold'>('Start');
  const [nextStep, setNextStep] = useState<{ progress: 'Start' | 'Complete'; hold: 'Resume' | 'On Hold' }>({
    progress: 'Start',
    hold: 'On Hold',
  });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Callbacks
  const handleModal = (nextAction: 'Start' | 'Complete' | 'Cancel' | 'Resume' | 'On Hold') => {
      setAction(nextAction);
      setIsOpen(true);
    },
    getStatusAction = () => {
      switch (action) {
        case 'Start':
          return WorkOrderStatus.InProgress;

        case 'Complete':
          return WorkOrderStatus.Completed;

        case 'Cancel':
          return WorkOrderStatus.Cancelled;

        case 'On Hold':
          return WorkOrderStatus.OnHold;

        case 'Resume':
          return workOrder.actualStart === undefined ? WorkOrderStatus.NotStarted : WorkOrderStatus.InProgress;
      }
    },
    handleAction = async () => {
      if (isRequestInProgress) return;

      setRequestInProgress(true);

      try {
        await updateWorkOrderStatus({
          id: workOrder.id,
          status: getStatusAction().replace(/\s/g, ''),
          actualStart: action == 'Complete' ? workFrom : undefined,
          actualEnd: action == 'Complete' ? workTo : undefined,
          actualCost: actualCost,
          hourReading:
            workOrder.product?.allowHourTracking && (action == 'Start' || action == 'Complete') && hourReading
              ? hourReading
              : undefined,
        });
      } catch (e: any) {
        if (e.status === 400) {
          toast(e.message, 'error');
        } else {
          toast('Error updating work order status', 'error');
        }
      }

      refresh();
      setRequestInProgress(false);
      setIsOpen(false);
    },
    isSubmitDisableed = () => {
      // Check if hour reading is not valid
      const hourReadingNotValid = workOrder.product?.allowHourTracking ? hourReading === undefined : false;

      if (action == 'Start') {
        return hourReadingNotValid;
      }

      if (action == 'Complete') {
        if (workOrder.user) {
          return workFrom === undefined || workTo === undefined || workFrom > workTo;
        } else {
          return (
            workFrom === undefined ||
            workTo === undefined ||
            workFrom > workTo ||
            hourReadingNotValid ||
            actualCost === undefined
          );
        }
      }

      return false;
    },
    close = () => {
      if (isRequestInProgress) return;
      setIsOpen(false);
    },
    validate = () => {
      const messages = [];
      if (workFrom && workTo && workFrom > workTo) {
        messages.push('Work End should be greater than Work Start');
      }

      setValidationErrors(messages);
    };

  // Hooks
  useEffect(() => {
    setNextStep({
      progress:
        (workOrder.status == WorkOrderStatus.OnHold && workOrder.actualStart !== undefined) ||
        workOrder.status == WorkOrderStatus.InProgress
          ? 'Complete'
          : 'Start',
      hold: workOrder.status == WorkOrderStatus.OnHold ? 'Resume' : 'On Hold',
    });
    setWorkFrom(workOrder.from);
    setWorkTo(workOrder.to || DateTime.now());
  }, [workOrder]);

  useEffect(() => {
    setHourReading(workOrder.unit?.lastHourReading?.hour || undefined);
  }, [workOrder.unit?.lastHourReading]);

  useEffect(() => {
    validate();
  }, [workFrom, workTo]);

  return (
    <>
      {/* Actions */}
      <div
        className={`flex pt-7 gap-2 flex-col sm:flex-row ${
          workOrder.status === WorkOrderStatus.Completed || workOrder.status === WorkOrderStatus.Cancelled
            ? 'justify-end'
            : 'justify-between'
        }`}
      >
        {workOrder.status === WorkOrderStatus.Completed || workOrder.status === WorkOrderStatus.Cancelled ? (
          !workOrder.user && (
            <div className="flex gap-x-10 text-sm">
              <Typography type="text-sm" weight="medium" className="text-grey-500">
                Actual Cost
              </Typography>
              <Typography type="text-sm">{displayMoneyFormat(workOrder.actualCost)}</Typography>
            </div>
          )
        ) : (
          <>
            <Button className="button-tertiary" onClick={() => handleModal('Cancel')}>
              Cancel
            </Button>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                className="button-secondary"
                disabled={workOrder.status === WorkOrderStatus.NotStarted}
                onClick={() => handleModal(nextStep.hold)}
              >
                {nextStep.hold}
              </Button>
              <Button className="button-primary" onClick={() => handleModal(nextStep.progress)}>
                {nextStep.progress}
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isOpen}
        headerIcon={<StatusIcon status={getStatusAction()} size="xlarge" />}
        title={action == 'On Hold' ? action : `${action} Work Order`}
        additionalText={
          action == 'Complete'
            ? 'After completion, any adjustments will be unavailable.'
            : `Are your sure you want to ${
                action == 'On Hold'
                  ? `put the work order ${action.toLowerCase()}`
                  : `${action.toLowerCase()} the work order`
              }
        ?`
        }
        actions={
          isRequestInProgress ? (
            <Spinner />
          ) : (
            <>
              <Button className="button-secondary" onClick={close}>
                {action == 'Cancel' ? 'No' : 'Cancel'}
              </Button>

              <Button className="button-primary" onClick={() => handleAction()} disabled={isSubmitDisableed()}>
                {action == 'Cancel' ? 'Yes' : action}
              </Button>
            </>
          )
        }
        close={close}
      >
        <ValidationError
          className={classNames({ hidden: validationErrors.length === 0 })}
          style={{ maxWidth: '342px' }}
        >
          <ul>
            {validationErrors.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </ValidationError>

        {action == 'Complete' && (
          <>
            <DateTimePicker
              label="Work Start"
              isRequired
              value={workFrom}
              onChange={setWorkFrom}
              timezone={location?.timezone}
            />

            <DateTimePicker
              label="Work End"
              isRequired
              value={workTo}
              onChange={setWorkTo}
              timezone={location?.timezone}
            />
          </>
        )}

        {action == 'Complete' && !workOrder.user && (
          <Field
            label="Actual Cost"
            isRequired
            type="currency"
            value={actualCost}
            onChange={(value) => setActualCost(value)}
            className="text-sm"
          />
        )}

        {(action == 'Start' || action == 'Complete') && workOrder.product?.allowHourTracking && (
          <Field
            label="Machine Hours"
            type="number"
            value={hourReading}
            onChange={(value) => setHourReading(value)}
            description={
              workOrder.unit?.lastHourReading
                ? `Last entry/ ${workOrder.unit.lastHourReading.trackedAt?.toFormat('DD')}`
                : undefined
            }
          />
        )}
      </Modal>
    </>
  );
};

const LastAndUpcomingOrders = ({ workOrder }: { workOrder: WorkOrder }) => {
  const { getProductSchedules } = useAPI();

  const [lastOrders, setLastOrders] = useState<OrderScheduleEntry[]>([]);
  const [upcomingOrders, setUpcomingOrders] = useState<OrderScheduleEntry[]>([]);

  useEffect(() => {
    const getSchedules = async () => {
      const from = workOrder.actualStart || workOrder.from;
      const to = workOrder.actualEnd || workOrder.to || from;

      if (!workOrder.product || !workOrder.unit) return;

      const prodSchedule = await getProductSchedules(
        [workOrder.product.id],
        [workOrder.unit.locationId],
        from.minus({ days: 7 }),
        to.plus({ days: 7 }),
      );
      const unitSchedule = prodSchedule[0].unitSchedules
        .find(({ unit }) => unit.id === workOrder.unit?.id)!
        .schedule.filter(({ type }) => type === 'Order') as OrderScheduleEntry[];

      setLastOrders(unitSchedule.filter((s) => s.orderReturnStatus === OrderReturnStatus.Complete));
      setUpcomingOrders(unitSchedule.filter((s) => s.orderReturnStatus === OrderReturnStatus.NotStarted));
    };

    getSchedules();
  }, [workOrder]);

  return (
    <div className="related-orders">
      {lastOrders.map(({ orderId, orderNumber, from, to }) => (
        <RelatedOrder
          key={`related-${orderId}-last`}
          type="Last"
          id={orderId}
          orderNumber={orderNumber}
          from={from}
          to={to}
        />
      ))}
      {upcomingOrders.map(({ orderId, orderNumber, from, to }) => (
        <RelatedOrder
          key={`related-${orderId}-upcoming`}
          type="Upcoming"
          id={orderId}
          orderNumber={orderNumber}
          from={from}
          to={to}
        />
      ))}
    </div>
  );
};

const RelatedOrder = ({
  type,
  id,
  orderNumber,
  from,
  to,
}: {
  type: 'Last' | 'Upcoming';
  id: number;
  orderNumber: string;
  from: DateTime;
  to?: DateTime;
}) => {
  return (
    <div className="order-entry">
      <Typography type="text-md" weight="semi-bold">
        {type} Order
      </Typography>

      <div className="h-[1px] w-full bg-grey-300 mt-[16px]"></div>

      <div className="field">
        <Typography type="text-sm" weight="medium" className="property text-grey-500">
          Invoice Number
        </Typography>
        <QuipliLink to={`orders/view/${id}`} className="text-quipli-black-800 underline">
          <Typography type="text-sm">{orderNumber}</Typography>
        </QuipliLink>
      </div>
      <div className="flex flex-col sm:flex-row justify-between">
        <div>
          <div className="field">
            <Typography type="text-sm" weight="medium" className="property text-grey-500">
              Order Start
            </Typography>
            <Typography type="text-sm" className="flex flex-grow gap-[12px] justify-between text-grey-800">
              <span>{from.toFormat('D')}</span>
              <span>{from.toFormat('t')}</span>
            </Typography>
          </div>
          <div className="field">
            <Typography type="text-sm" weight="medium" className="property text-grey-500">
              Order End
            </Typography>
            <Typography type="text-sm" className="flex flex-grow gap-[12px] justify-between text-grey-800">
              {to ? (
                <>
                  <span>{to.toFormat('D')}</span>
                  <span>{to.toFormat('t')}</span>
                </>
              ) : (
                <span>Returned</span>
              )}
            </Typography>
          </div>
        </div>

        <div className="pt-[16px] sm:pt-0 self-start sm:self-end">
          <QuipliLink to={`orders/view/${id}`} className="no-underline">
            <Button className="button-secondary">See details</Button>
          </QuipliLink>
        </div>
      </div>
    </div>
  );
};

const Activity = ({ workOrder }: { workOrder: WorkOrder }) => {
  const { getWorkOrderNoteFiles, addWorkOrderNoteFile, getWorkOrderFileUploadUrl, putWorkOrderFile } = useAPI();
  const admin = useAdmin();

  const [isListLoading, setListLoading] = useState<boolean>(false);
  const [isNewNoteLoading, setNewNoteLoading] = useState<boolean>(false);
  const [objects, setObjects] = useState<WorkOrderNoteFile[]>([]);
  const [items, setItems] = useState<ActivityItemProps[]>([]);
  const [note, setNote] = useState<string>('');
  const [file, setFile] = useState<File>();
  const [error, setError] = useState<string>('');

  const getFiles = async () => {
    setListLoading(true);
    const files = await getWorkOrderNoteFiles(workOrder.id);

    setObjects(files);
    setListLoading(false);
  };
  const addObject = async () => {
    if (!note.length && !file) return;

    setNewNoteLoading(true);
    setError('');

    if (file) {
      try {
        const signedUrl = await getWorkOrderFileUploadUrl(workOrder.id, file.name);
        await putWorkOrderFile(signedUrl, file);
      } catch (error) {
        setError('Something went wrong.');
        setNewNoteLoading(false);
        return;
      }
    }

    const result = await addWorkOrderNoteFile(workOrder.id, admin.id, note, file?.name);

    setObjects((prevState) => {
      setNote('');
      setFile(undefined);
      setNewNoteLoading(false);
      return [...prevState, result];
    });
  };

  useEffect(() => {
    getFiles();
  }, []);

  useEffect(() => {
    const entries: ActivityItemProps[] = [
      {
        date: workOrder.createdAt,
        text: (
          <span>
            <Typography type="text-sm" className="text-grey-500 inline">
              Work order
            </Typography>{' '}
            <Typography type="text-sm" className="text-grey-800 inline" weight="medium">
              created.
            </Typography>
          </span>
        ),
      },
    ];

    if (workOrder.actualStart)
      entries.push({
        text: (
          <span>
            <Typography type="text-sm" className="text-grey-500 inline">
              Status changed to
            </Typography>{' '}
            <Typography type="text-sm" className="text-grey-800 inline" weight="medium">
              {WorkOrderStatus.InProgress}.
            </Typography>
          </span>
        ),
        color: 'warning',
        date: workOrder.actualStart,
      });

    if (workOrder.actualEnd)
      entries.push({
        text: (
          <span>
            <Typography type="text-sm" className="text-grey-500 inline">
              Work order
            </Typography>{' '}
            <Typography type="text-sm" className="text-grey-800 inline" weight="medium">
              completed.
            </Typography>
          </span>
        ),
        color: 'success',
        date: workOrder.actualEnd,
      });

    entries.push(
      ...objects.map((at) => ({
        date: at.uploadedAt,
        object: at,
        text: (
          <Typography type="text-sm" className="text-grey-800 capitalize">
            {at.uploadedBy.adminFullName}.
          </Typography>
        ),
      })),
    );

    entries.sort((a, b) => a.date.toMillis() - b.date.toMillis());

    setItems(entries);
  }, [workOrder.actualStart, workOrder.actualEnd, objects]);

  return (
    <div className="col-span-2 pb-8 pt-4 px-5 md:px-16 border-0">
      <Typography type="display-xs" weight="medium" className="text-grey-800">
        Activity
      </Typography>

      {isListLoading ? (
        <Spinner />
      ) : (
        <>
          <Timeline
            includeLastItemLine
            items={items.map((item, index) => ({
              type: item.color,
              dotWrapperClassName: item.object ? 'large-custom-dot' : undefined,
              customDot: item.object ? <ActivityItemDot object={item.object} /> : undefined,
              content: <ActivityItem key={`timeline-item-${index}`} {...item} reload={getFiles} />,
            }))}
          />

          <div className="new-note-section">
            <div className="avatar-initials">
              <Typography className="text-grey-500" type="text-sm">
                {admin.initials}
              </Typography>
            </div>

            <div className="flex-grow form-input note-shadow !p-[14px]">
              <TextArea
                value={note}
                onChange={({ target }) => setNote(target.value)}
                rows={3}
                className="border-none w-full font-sans text-sm"
                placeholder="Leave a note..."
              />

              {error.length > 0 && (
                <Typography type="text-xs" className="text-error-500">
                  {error}
                </Typography>
              )}

              {file && (
                <div className="flex items-center justify-between gap-x-[4px] pb-[12px]">
                  <Typography type="text-sm" className="text-grey-400">
                    {file.name}
                  </Typography>
                  <div className="cursor-pointer" onClick={() => setFile(undefined)}>
                    <Icon icon="Close" size="small" />
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center">
                {isNewNoteLoading ? (
                  <div className="w-full flex justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <label className="cursor-pointer" htmlFor="test-file">
                      <Icon icon="PaperClip" stroke="var(--grey-400)" />
                      <input
                        onChange={({ target }) => target.files && target.files[0] && setFile(target.files[0])}
                        type="file"
                        id="test-file"
                        className="hidden"
                      />
                    </label>

                    <div className="flex-shrink-0">
                      <Button className="button-secondary" onClick={addObject} disabled={!note.length && !file}>
                        <Typography className="text-grey-800" type="text-sm" weight="semi-bold">
                          Add Note
                        </Typography>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ActivityItem = ({ object, date, text, reload }: ActivityItemProps & { reload: () => void }) => {
  const { downloadWorkOrderFile, updateWorkOrderNote, deleteWorkOrderNoteFile } = useAPI();
  const admin = useAdmin();

  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [note, setNote] = useState<string>(object?.note || '');

  const downloadFile = async () => {
    if (!object?.file) return;

    const url = await downloadWorkOrderFile(object.workOrderId, object.file);

    window.open(url, '_blank');
  };
  const removeItem = async () => {
    if (!object) return;

    await deleteWorkOrderNoteFile(object.workOrderId, object.id);
    setModalOpen(false);
    reload();
  };
  const updateNote = async () => {
    if (!object) return;

    setLoading(true);
    await updateWorkOrderNote(object.workOrderId, object.id, note);

    setLoading(false);
    setIsEditing(false);
    reload();
  };

  return (
    <>
      <div className="item-content-wrapper w-full">
        <div className={classNames('w-full', { 'attachment-note': !!object })}>
          <div className="flex items-center justify-between">
            <div className="label-and-date">
              {text}
              <Typography type="text-sm" className="text-grey-500">
                {date.toFormat('ff')}
              </Typography>
            </div>

            {object && !isEditing && (object.uploadedBy.adminId === admin.id || admin.isSuperAdmin) && (
              <div className="relative">
                <Dropdown
                  trigger={() => (
                    <Button className="button-tertiary circle">
                      <Icon icon="DotsHorizontal" stroke="var(--grey-800)" size="small" />
                    </Button>
                  )}
                >
                  <div
                    className="flex gap-[9px] items-center"
                    onClick={() => {
                      setIsEditing(true);
                    }}
                  >
                    <Icon icon="Edit" />
                    <Typography type="text-sm">Edit</Typography>
                  </div>

                  <div
                    className="flex gap-[9px] items-center"
                    onClick={() => {
                      setModalOpen(true);
                    }}
                  >
                    <Icon icon="Trash" stroke="var(--error-500)" />
                    <Typography type="text-sm" className="text-error-500">
                      Delete
                    </Typography>
                  </div>
                </Dropdown>
              </div>
            )}
          </div>

          {object && (
            <>
              {object.file && (
                <div
                  className="w-full mt-[8px] p-[16px] bg-grey-50 border-grey-200 border-[1px] border-solid rounded-[8px] cursor-pointer flex items-center gap-[16px]"
                  onClick={() => downloadFile()}
                >
                  <div className="w-[32px] h-[32px] rounded-full bg-grey-100 flex justify-center items-center">
                    <Icon icon="File" stroke="var(--grey-600)" />
                  </div>

                  <Typography type="text-sm" weight="medium" className="text-grey-700">
                    {object.file}
                  </Typography>
                </div>
              )}
              {(object.note || isEditing) && (
                <div className="mt-[8px]">
                  {isEditing ? (
                    <div>
                      <TextArea
                        value={note}
                        onChange={({ target }) => setNote(target.value)}
                        className="border-none w-full font-sans text-sm"
                        autoFocus={true}
                      />

                      <div className="button-group">
                        {isLoading ? (
                          <Spinner />
                        ) : (
                          <>
                            <Button className="button-secondary" onClick={() => setIsEditing(false)}>
                              Cancel
                            </Button>
                            <Button className="button-primary" onClick={() => updateNote()}>
                              Update
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Typography type="text-sm" className="text-grey-900 whitespace-pre-line">
                      {object.note}
                    </Typography>
                  )}
                </div>
              )}

              <ConfirmModal
                isOpen={isModalOpen}
                close={() => setModalOpen(false)}
                onConfirm={removeItem}
                headerIcon={
                  <div className="flex justify-center">
                    <Icon icon="XCircle" size="xlarge" stroke="var(--error-500)" />
                  </div>
                }
                prompt="Are you sure you want to delete this?"
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

const ActivityItemDot = ({ object }: { object: WorkOrderNoteFile }) => {
  const getUserInitials = () => {
    const names = object.uploadedBy.adminFullName.toLocaleUpperCase().split(' ');
    return names.length > 1 ? `${names[0][0]}${names[1][0]}` : `${names[0].substring(0, 2)}`;
  };

  return (
    <Tooltip
      tooltip={
        <Typography type="text-xs" className="capitalize !p-2">
          {object.uploadedBy.adminFullName}
        </Typography>
      }
      type="black"
    >
      <div className="avatar-initials mt-[8px]">
        <Typography className="text-grey-500" type="text-sm">
          {getUserInitials()}
        </Typography>
      </div>
    </Tooltip>
  );
};

const StatusIcon = ({ status, size = 'medium' }: { status: WorkOrderStatus; size?: 'medium' | 'xlarge' }) => {
  const iconType = (): {
    icon: 'NotStarted' | 'InProgress' | 'Complete' | 'InfoCircle';
    stroke?: string;
    size: 'medium' | 'xlarge';
  } => {
    switch (status) {
      case WorkOrderStatus.OnHold:
        return { icon: 'InProgress', stroke: 'var(--grey-400)', size };
      case WorkOrderStatus.InProgress:
        return { icon: 'InProgress', stroke: 'var(--warning-300) ', size };
      case WorkOrderStatus.Completed:
        return { icon: 'Complete', stroke: 'var(--success-500)', size };
      case WorkOrderStatus.Cancelled:
        return { icon: 'InfoCircle', stroke: 'var(--error-500)', size };
      case WorkOrderStatus.NotStarted:
      default:
        return { icon: 'NotStarted', stroke: 'var(--grey-400)', size };
    }
  };

  return (
    <div className="w-full flex items-center justify-center">
      <Icon {...iconType()} />
    </div>
  );
};
